import { Controller } from "@hotwired/stimulus"
import AirDatepicker from "air-datepicker"
import localeEn from "air-datepicker/locale/en"

// Renders the certification "Valid From / Valid To" fields as UK-formatted
// (dd/mm/yyyy) date pickers. Native <input type="date"> follows the browser's
// locale, so we can't force a UK display there; instead each field is a
// readonly text input the picker controls, paired with a hidden input that
// carries the ISO (yyyy-mm-dd) value Rails actually persists — set via
// AirDatepicker's altField, so display locale never affects what's submitted.
//
// Picking a "Valid From" defaults "Valid To" to 12 months later (most issuers
// grant a year's validity at a time), unless a "Valid To" is already set.
const UK_LOCALE = { ...localeEn, firstDay: 1 } // Monday-first week

export default class extends Controller {
  static targets = ["fromDisplay", "fromValue", "toDisplay", "toValue"]
  static values = { months: { type: Number, default: 12 } }

  connect() {
    this.toPicker = this.buildPicker(this.toDisplayTarget, this.toValueTarget)
    this.fromPicker = this.buildPicker(this.fromDisplayTarget, this.fromValueTarget, {
      onSelect: ({ date }) => this.autofillTo(date)
    })
  }

  disconnect() {
    this.fromPicker?.destroy()
    this.toPicker?.destroy()
  }

  buildPicker(displayEl, valueEl, extra = {}) {
    return new AirDatepicker(displayEl, {
      locale: UK_LOCALE,
      dateFormat: "dd/MM/yyyy",
      altField: valueEl,
      altFieldDateFormat: "yyyy-MM-dd",
      autoClose: true,
      // Modal and slideover are <dialog showModal()> (top layer): a calendar
      // appended to <body> would render behind them, so anchor it inside the
      // dialog — which also keeps it clear of the scrollable form-fields.
      container: this.element.closest("dialog") || document.body,
      selectedDates: this.initialDate(valueEl),
      onSelect: (params) => {
        // Let the wizard's required-field validation react to the change.
        displayEl.dispatchEvent(new Event("input", { bubbles: true }))
        extra.onSelect?.(params)
      }
    })
  }

  // Seed the picker from a server-rendered ISO value (edit / failed submit).
  // Built from parts, not Date.parse, to stay timezone-agnostic.
  initialDate(valueEl) {
    const [year, month, day] = (valueEl.value || "").split("-").map(Number)
    if (!year || !month || !day) return []
    return [new Date(year, month - 1, day)]
  }

  autofillTo(fromDate) {
    if (!fromDate || this.toValueTarget.value) return
    // Date normalises month overflow, so +12 rolls the year over correctly.
    const target = new Date(fromDate.getFullYear(), fromDate.getMonth() + this.monthsValue, fromDate.getDate())
    this.toPicker.selectDate(target)
  }
}
